import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminInvestmentDocumentCreationRequest,
  AdminProductDistributionPlatformListing,
  DocumentsApi,
  AdminInvestmentDocumentCreationDocumentTypeEnum,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Button from 'ui/atoms/button';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import Spacer from 'ui/atoms/spacer';
import * as Styled from './styled';
import Tooltip, { TooltipPosition } from 'ui/atoms/tooltip';
import Icon from 'ui/atoms/icon';
import useIsMedia, { DEVICES } from 'ui/hooks/use-is-media';
import { useTranslateWithStringArgs } from 'ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'ui/helper/case-transforms';
import { FileWithDocType } from 'ui/atoms/file-upload';
import Hint from 'ui/atoms/hint';
import { useMultipleSingleFileUploads } from 'src/ui/atoms/file-upload/hooks/use-multiple-single-file-uploads';

interface AddInvestmentDocumentsModalProps {
  distributionPlatforms: AdminProductDistributionPlatformListing[];
  onClose: () => void;
  onInvestmentDocumentsAdded: () => void;
}

interface AddInvestmentDocumentsFields {
  distributionPlatform: string;
  investmentDocuments: FileWithDocType[];
}

export const Form = makeModalForm<AddInvestmentDocumentsFields>();

const AddInvestmentDocumentsModal: FunctionComponent<AddInvestmentDocumentsModalProps> = ({
  distributionPlatforms,
  onClose,
  onInvestmentDocumentsAdded,
}) => {
  const [selectedPlatform, setSelectedPlatform] = useState<null | string>(null);

  const { makeAuthenticatedApi, withApi, loading } = useApiCall();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const translate = useTranslateWithStringArgs();

  const { documentsUploaded, onChangeDocument, addMoreDocuments, error, setError } = useMultipleSingleFileUploads();

  const isPhone = useIsMedia(DEVICES.phone);

  const validateDocuments = useCallback(
    (filteredDocuments: FileWithDocType[]) => {
      if (!filteredDocuments?.length) {
        setError(translate('addInvestmentDocuments.errors.noDocument'));
        return false;
      }

      if (filteredDocuments.some((document) => !document.documentType)) {
        setError(translate('addInvestment.errors.noDocumentType'));
        return false;
      }

      const checkedDocumentTypes = new Set();

      const documentTypesToSubmit = [...filteredDocuments.map((doc) => doc.documentType)];

      const isDuplicateType = documentTypesToSubmit.some((documentType) => {
        if (checkedDocumentTypes.has(documentType)) {
          setError(translate('addInvestmentDocuments.errors.multipleDocsForDocType'));
          return true;
        }

        checkedDocumentTypes.add(documentType);
        return false;
      });

      if (isDuplicateType) {
        return false;
      }

      return true;
    },
    [setError, translate],
  );

  const uploadFiles = useCallback(
    async (documents: FileWithDocType[]) => {
      let documentsRequest: AdminInvestmentDocumentCreationRequest[] = [];

      await Promise.all(
        documents.map(async (document: FileWithDocType) => {
          if (!document.documentType || !document.file) {
            documentsRequest = [];
            return documentsRequest;
          }

          const documentResponse = await documentsApi.adminDocumentsCreate({
            file: document.file,
          });
          documentsRequest.push({
            documentId: documentResponse.id,
            documentType: document.documentType as AdminInvestmentDocumentCreationDocumentTypeEnum,
          });
        }),
      );

      return documentsRequest;
    },
    [documentsApi],
  );

  const onSubmit = useCallback(
    async (values: StateValues<AddInvestmentDocumentsFields>) => {
      const filteredDocuments = documentsUploaded.filter((doc) => doc.document.file).map((doc) => doc.document);
      const areDocumentsValid = validateDocuments(filteredDocuments);
      if (!areDocumentsValid) return;

      const documents: AdminInvestmentDocumentCreationRequest[] = await uploadFiles(filteredDocuments);

      if (!documents?.length) return;

      withApi(async () => {
        await adminApi.adminListingsInvestmentDocumentsCreate({
          id: values.distributionPlatform,
          adminBulkInvestmentDocumentCreationRequest: { documents },
        });

        onInvestmentDocumentsAdded();
      });
    },
    [JSON.stringify(documentsUploaded)],
  );

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addInvestmentDocuments"
        onSubmit={(values: StateValues<AddInvestmentDocumentsFields>) => {
          onSubmit(values);
        }}
        initial={{
          distributionPlatform: distributionPlatforms?.length === 1 ? distributionPlatforms[0].id : undefined,
        }}
      >
        <ModalHeader>
          <Translate name="addInvestmentDocuments.button" />
        </ModalHeader>
        <ModalContent>
          <Form.Group name="distributionPlatform" required={true}>
            <Form.Select
              options={distributionPlatforms.map((platform) => ({
                value: platform.id,
                label: platform.distributionPlatformDisplayName,
              }))}
              disabled={distributionPlatforms?.length === 1}
              onChange={(platform: string) => {
                setSelectedPlatform(platform);
              }}
            ></Form.Select>
          </Form.Group>
          <Styled.SameRowWithTooltip>
            <Styled.LabelTooltip>
              <Translate name="addInvestmentDocuments.investmentDocuments" />
            </Styled.LabelTooltip>
            <Tooltip
              content={<Translate name="addInvestmentDocuments.tooltip" />}
              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
            >
              <Icon name="question" circular={true} size="large" color="info" />
            </Tooltip>
            <Spacer y={2} />
          </Styled.SameRowWithTooltip>
          <Form.Group name="investmentDocuments" label={null}>
            {documentsUploaded &&
              documentsUploaded.map((documentUploaded, index) => {
                return (
                  <>
                    <Form.FileUpload
                      key={documentUploaded.index}
                      onChange={(document: FileWithDocType) => onChangeDocument(document, documentUploaded.index)}
                      documentTypes={Object.values(AdminInvestmentDocumentCreationDocumentTypeEnum).map(
                        (documentType) => ({
                          label: translate(`documentTypes.${snakeToCamelCaseWithNumbers(documentType)}`),
                          value: documentType,
                        }),
                      )}
                    />
                    {index !== documentsUploaded.length - 1 && <Spacer y={4} />}
                  </>
                );
              })}
          </Form.Group>
          {!!error?.length && (
            <Hint variant="danger">
              <Translate name={error} />
            </Hint>
          )}
          <Button variant="link" icon="plus" size="small" inline iconSize="small" onClick={addMoreDocuments}>
            <Translate name="addInvestmentDocuments.addFurtherDocuments" />
          </Button>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: onClose,
              },
              {
                name: 'save',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
                disabled: selectedPlatform === null && distributionPlatforms.length > 1,
                loading: loading,
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddInvestmentDocumentsModal;
